import { useParams } from "@/components/hooks/use-params";
import { useRoute } from "@/components/shared/router";
import type { AddressFormData } from "@/store/auth/forms/useAddressForm";
import { useAddressForm } from "@/store/auth/forms/useAddressForm";
import { useUpdateAddressMutation } from "@/gqlhooks/profile/useUpdateAddressMutation";
import {
  createUnitNumber,
  getErrorMessage,
  getUnitNumber,
  sanitizeOptionalText,
} from "@/lib/helpers/string";
import { useEffect } from "react";
import { showToast } from "@/components/ui/toast/show-toast";
import { FormProvider } from "react-hook-form";
import { UpdateAddressScreen } from "./update-address-screen";
import { useClientAddressesQuery } from "@/gqlhooks/profile/useClientAddressesQuery";
import { useApolloClient } from "@apollo/client";
import type { UpdateAddressProps } from "@/types/profile";

export { type UpdateAddressProps };

export const UpdateAddress = () => {
  const { id } = useParams<{ id: string }>();
  const { push, pull } = useRoute();
  const client = useApolloClient();

  const { data: addresses, loading: addressesLoading } =
    useClientAddressesQuery();
  const address = addresses?.find((address) => address.id === id);
  const isEdit = !!address?.primary;

  useEffect(() => {
    if (!addressesLoading && !address) {
      push({
        pageKey: "addresses",
      });
    }
  }, [address, addressesLoading]);

  const currentValues: AddressFormData = {
    label: address?.label ?? "",
    postalCode: address?.postalCode ?? "",
    fullAddress: address?.fullAddress ?? "",
    ...getUnitNumber(address?.unitNumber || undefined),
    primary: address?.primary ?? false,
  };

  const form = useAddressForm(currentValues);
  const { handleSubmit } = form;

  const [updateAddressMutation, { loading: updateAddressLoading }] =
    useUpdateAddressMutation();

  const onSubmit = handleSubmit(async (data: AddressFormData) => {
    try {
      if (!id) return;

      const result = await updateAddressMutation({
        variables: {
          input: {
            id: id,
            label: sanitizeOptionalText(data.label),
            fullAddress: data.fullAddress,
            postalCode: data.postalCode,
            unitNumber: createUnitNumber(data.unitFloor, data.apartmentNumber),
            primary: data.primary ?? false,
          },
        },
      });

      if (result.data?.updateAddress?.address) {
        // Evict and refetch the addresses cache
        client.cache.evict({ fieldName: "clientAddresses" });
        client.cache.gc();
        pull();
        showToast({
          type: "success",
          title: "Success update address",
        });
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error update address");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  });

  if (addressesLoading || !address) {
    return null; // Or return a loading component / Will redirect via useEffect if !address
  }

  return (
    <FormProvider {...form}>
      <UpdateAddressScreen
        onSubmit={onSubmit}
        updateAddressLoading={updateAddressLoading}
        showTooltip={isEdit}
      />
    </FormProvider>
  );
};
